<div id="{id}" class="prom-file-select">
    <label>
        <span class="prom-input-field__label">
            {labelText}
            {#if required}
                <span aria-hidden="true" class="prom-input__required-star"
                    >*</span>
            {/if}
        </span>

        <button
            class="prom-file-select__drop-zone"
            class:prom-file-select__drop-zone--dragging={isDragging}
            class:prom-file-select__drop-zone--dropdown={dropdownExpanded}
            type="button"
            id="prom-file-select__{id}"
            bind:this={addFileButtonElement}
            aria-haspopup="true"
            on:click|stopPropagation={toggleDropdown}
            on:dragover|preventDefault|stopPropagation={dragOver}
            on:dragenter|preventDefault|stopPropagation={dragOver}
            on:dragleave|preventDefault|stopPropagation={stopDrag}
            on:dragend|preventDefault|stopPropagation={stopDrag}
            on:drop|preventDefault|stopPropagation={dropped}
        >
            <span class="prom-file-select__upload-text">
                {multiple ? i18n.addFiles : i18n.addFile}
            </span>
        </button>

        <input
            class="prom-file-select__file-input"
            bind:this={fileInputElement}
            type="file"
            {multiple}
            {required}
            hidden
            on:change={filesPicked}
            {accept}
        />
    </label>

    <div
        aria-live={ariaLiveUpdateType}
        class="prom-error-message"
        id="{errorMessageId}">
        {#if hasError && errorMessage}
            {errorMessage}
        {/if}
    </div>

    <div bind:this={dropdownContainerElement} role="application" aria-hidden={!dropdownExpanded} class="prom-file-select__dropdown">
        <Dropdown
            id="prom-file-select-dropdown"
            arrow={true}
            arrowPosition="top"
            bind:expanded={dropdownExpanded}
            items={dropdownItems}
            on:selectedOption={(event) => optionSelected(event.detail.data)}
            on:dropdownClose={closeHandler}
        ></Dropdown>
    </div>
</div>

<script>
    import { isMobile, isChromeBook } from '@itslearning/atlas/detectors/UserAgentHelper';
    import { iconClassForFile } from '@itslearning/atlas/files/fileIcons';
    import { guidV4 } from '@itslearning/atlas/generate/guid';

    import Dropdown from '../../../dropdowns/Dropdown/v1/Dropdown.svelte';

    import { createEventDispatcher, tick } from 'svelte';

    const dispatch = createEventDispatcher();
    const id = guidV4();

    /** @type {any} */
    export let i18n = {
        addFile: undefined,
        addFiles: undefined,
        yourDevice: undefined,
        yourComputer: undefined,
    };

    /** @type {boolean} */
    export let multiple = true;
    /** @type {string} */
    export let accept = undefined;
    /** @type {any[]} */
    export let cloudOptions = [];
    /** @type {string} */
    export let labelText = "";
    /** @type {boolean} */
    export let required = false;
    /** @type {boolean} */
    export let hasError = false;
    /** @type {string} */
    export let errorMessage = undefined;
    /** @type {undefined|'off'|'polite'|'assertive'} */
    export let ariaLiveUpdateType = null;

    let dropdownExpanded = false;
    let isDragging = false;

    let addFileButtonElement;
    let fileInputElement;
    let dropdownContainerElement;

    $: errorMessageId = id + '__error-message';

    $: dropdownItems = [
        { options: [
            fileUploadOption,
            ...dropdownCloudOptions,
        ].filter(o => o !== null) }
    ];

    let dropdownCloudOptions;

    $: dropdownCloudOptions = cloudOptions.map(option => {
        const menuOption = {
            icon: option.icon,
            iconsize: 24,
            label: option.label,
            link: false,
            value: { source: 'cloud', cloudOption: option }
        };

        if (option.recentFiles && option.recentFiles.length) {
            menuOption.items = [
                {
                    options: [
                        {
                            label: i18n.selectFileLabel,
                            link: false,
                            value: { source: 'cloud', cloudOption: option },
                            divider: true,
                            ellipsis: true,
                        }
                    ]
                },
                {
                    label: i18n.recent,
                    options: option.recentFiles.map(item => {
                        return {
                            cssClass: 'prom-file-select__file-icon ' +
                                iconClassForFile(item.name, item.mimeType, option.sourceType),
                            iconsize: 24,
                            value: {
                                source: 'cloud',
                                cloudOption: option,
                                file: item,
                            },
                            label: item.name,
                            link: false,
                        };
                    }),
                }
            ];
        }

        return menuOption;
    });

    let fileUploadOption;

    $: {
        if (!isChromeBook()) {
            const mobile = isMobile();

            fileUploadOption = {
                cssClass: mobile ? 'prom-file-select__device-icon' : 'prom-file-select__computer-icon',
                iconsize: 24,
                label: mobile ? i18n.yourDevice : i18n.yourComputer,
                link: false,
                value: { source: 'computer' }
            };
        } else {
            fileUploadOption = null;
        }
    }

    function optionSelected(value) {
        if (value.source === 'computer') {
            fileInputElement.click();
        } else if (value.source === 'cloud') {
            if (value.file) {
                dispatch('cloudFilePicked', value);
            } else {
                dispatch('cloudOptionClicked', value);
            }
        }
    }

    function filesPicked() {
        const files = [];

        for (let i = 0; i < fileInputElement.files.length; i++) {
            files.push(fileInputElement.files[i]);
        }

        if (files.length > 0) {
            dispatch('localFilesPicked', { files });

            // Clear the file input after getting the files or else if the user
            // chooses the same file again the on:change event will not fire
            fileInputElement.value = null;
        }
    }

    function closeHandler() {
        if (dropdownExpanded) {
            dropdownExpanded = false;

            addFileButtonElement.focus();
        }
    }

    function dragOver() {
        isDragging = true;
    }

    function stopDrag() {
        isDragging = false;
    }

    function dropped(event) {
        isDragging = false;

        if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            const files = [];

            for (let i = 0; i < event.dataTransfer.files.length; i++) {
                files.push(event.dataTransfer.files[i]);
            }

            dispatch('localFilesPicked', { files });
        }
    }

    async function toggleDropdown() {
        if (dropdownItems[0].options.length === 1) {
            optionSelected(dropdownItems[0].options[0].value);
            addFileButtonElement.focus();
        } else {
            dropdownExpanded = !dropdownExpanded;
    
            if (dropdownExpanded) {
                await tick();
                dropdownContainerElement.querySelector('button').focus();
            } else {
                addFileButtonElement.focus();
            }
        }
    }
</script>
